import React, { useState, useEffect } from 'react';
import API from '../services/api';

const C = {
  navy:    '#1D2C5F',
  copper:  '#C67A52',
  muted:   '#878DA1',
  bg:      '#F5F3EF',
  white:   '#FFFFFF',
  text:    '#1a1f2e',
};

const STATUS_META = {
  UNPAID:    { bg: '#FEF3C7', color: '#92400E', label: 'Unpaid' },
  PAID:      { bg: '#D1FAE5', color: '#065F46', label: 'Paid' },
  DEBT:      { bg: '#FEE2E2', color: '#991B1B', label: 'Debt' },
  SUBMITTED: { bg: '#EEF2FF', color: '#1D2C5F', label: 'Submitted' },
  APPROVED:  { bg: '#D1FAE5', color: '#065F46', label: 'Approved' },
};

function StatusBadge({ status }) {
  const key = (status || '').toUpperCase();
  const m = STATUS_META[key] || { bg: '#F3F4F6', color: '#374151', label: status || '—' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 9px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      backgroundColor: m.bg,
      color: m.color,
      whiteSpace: 'nowrap',
    }}>
      {m.label}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function fmtMoney(v) {
  return `$${(parseFloat(v) || 0).toFixed(2)}`;
}

export default function TherapistBillingHistoryPage() {
  const [clients, setClients]     = useState([]);
  const [clientId, setClientId]   = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [results, setResults]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => {
    API.get('/therapist/clients?includeInactive=true')
      .then(r => setClients(r.data || []))
      .catch(() => {});
  }, []);

  const search = async (e) => {
    e.preventDefault();
    if (!clientId && !startDate && !endDate) {
      setError('Select a client or enter a date range to search.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (clientId)  p.append('clientId',  clientId);
      if (startDate) p.append('startDate', startDate);
      if (endDate)   p.append('endDate',   endDate);
      const r = await API.get(`/therapist/billing-history?${p}`);
      setResults(r.data || []);
    } catch {
      setError('Failed to load billing history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setClientId('');
    setStartDate('');
    setEndDate('');
    setResults(null);
    setError('');
  };

  const summary = results
    ? results.reduce((a, r) => {
        a.count++;
        a.billed    += parseFloat(r.billed_amount)    || 0;
        a.collected += parseFloat(r.collected_amount) || 0;
        return a;
      }, { count: 0, billed: 0, collected: 0 })
    : null;

  return (
    <div style={{ padding: '1.5rem 2rem', background: C.bg, minHeight: '100vh' }}>
      <h2 style={{ margin: '0 0 1.25rem', color: C.navy, fontSize: '1.3rem', fontWeight: 700 }}>
        Billing History
      </h2>
      <p style={{ margin: '-0.75rem 0 1.25rem', color: C.muted, fontSize: '0.88rem' }}>
        Look up previously submitted sessions. Select a client, a date range, or both.
      </p>

      {/* ── Filter card ─────────────────────────────────────────── */}
      <div style={card}>
        <form onSubmit={search}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            gap: '1rem',
            alignItems: 'flex-end',
          }}>
            <div>
              <label style={lbl}>Client</label>
              <select value={clientId} onChange={e => setClientId(e.target.value)} style={inp}>
                <option value="">All Clients</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.client_name}{c.is_active ? '' : ' (inactive)'}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={lbl}>From Date</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inp} />
            </div>

            <div>
              <label style={lbl}>To Date</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={inp} />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', paddingTop: '1.4rem' }}>
              <button type="submit" disabled={loading} style={btnPrimary}>
                {loading ? 'Searching…' : 'Search'}
              </button>
              {results !== null && (
                <button type="button" onClick={clear} style={btnGhost}>
                  Clear
                </button>
              )}
            </div>
          </div>
        </form>

        {error && (
          <p style={{ color: '#c0392b', margin: '0.75rem 0 0', fontSize: '0.88rem' }}>{error}</p>
        )}
      </div>

      {/* ── Summary pills ───────────────────────────────────────── */}
      {summary && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '0.85rem',
          marginBottom: '1.25rem',
        }}>
          <Pill label="Entries" value={summary.count} />
          <Pill label="Total Billed"     value={fmtMoney(summary.billed)} />
          <Pill label="Total Collected"  value={fmtMoney(summary.collected)} />
          <Pill label="Outstanding"      value={fmtMoney(summary.billed - summary.collected)}
                valueColor={summary.billed - summary.collected > 0 ? '#991B1B' : '#065F46'} />
        </div>
      )}

      {/* ── Results ─────────────────────────────────────────────── */}
      {results !== null && (
        results.length === 0
          ? (
            <div style={{ ...card, textAlign: 'center', padding: '3rem', color: C.muted, fontSize: '0.95rem' }}>
              No entries found for the selected filters.
            </div>
          )
          : (
            <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.87rem' }}>
                  <thead>
                    <tr>
                      {['Date', 'Client', 'Service / Type', 'Billed', 'Collected', 'Payment', 'Status', 'Notes'].map(h => (
                        <th key={h} style={th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((row, i) => {
                      const isFlag = row.no_show || row.cancelation;
                      const serviceLabel = row.no_show
                        ? 'No Show'
                        : row.cancelation
                        ? 'Cancellation'
                        : row.service_type || '—';
                      const flagStyle = isFlag
                        ? { color: '#92400E', fontStyle: 'italic' }
                        : {};

                      return (
                        <tr key={row.id} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAF9' }}>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(row.session_date)}</td>
                          <td style={{ ...td, fontWeight: 500 }}>{row.client_name}</td>
                          <td style={{ ...td, ...flagStyle }}>{serviceLabel}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtMoney(row.billed_amount)}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtMoney(row.collected_amount)}</td>
                          <td style={{ ...td }}>{row.payment_type || '—'}</td>
                          <td style={{ ...td }}><StatusBadge status={row.status} /></td>
                          <td style={{ ...td, maxWidth: 220, whiteSpace: 'normal', color: C.muted, fontSize: '0.8rem' }}>
                            {row.notes || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )
      )}
    </div>
  );
}

function Pill({ label, value, valueColor }) {
  return (
    <div style={{
      background: '#fff',
      borderRadius: 8,
      padding: '0.7rem 1rem',
      boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
      border: '1px solid #E5E7EB',
    }}>
      <div style={{ fontSize: '0.7rem', color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: '1.2rem', fontWeight: 700, color: valueColor || C.navy }}>
        {value}
      </div>
    </div>
  );
}

/* ── Shared styles ───────────────────────────────────────────────── */
const card = {
  background: C.white,
  borderRadius: 10,
  padding: '1.25rem 1.5rem',
  boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
  marginBottom: '1.25rem',
};

const lbl = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '0.3rem',
};

const inp = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: '0.9rem',
  color: C.text,
  background: '#fff',
  boxSizing: 'border-box',
};

const btnPrimary = {
  padding: '0.52rem 1.25rem',
  background: C.navy,
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '0.9rem',
  whiteSpace: 'nowrap',
};

const btnGhost = {
  padding: '0.52rem 0.9rem',
  background: 'transparent',
  color: C.muted,
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: '0.9rem',
  whiteSpace: 'nowrap',
};

const th = {
  padding: '0.7rem 1rem',
  background: C.navy,
  color: '#fff',
  fontWeight: 600,
  fontSize: '0.75rem',
  textAlign: 'left',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const td = {
  padding: '0.6rem 1rem',
  borderBottom: '1px solid #F3F4F6',
  color: C.text,
};
